import { useCallback, useEffect, useState } from "react";
import "../VerificationPros/verification_pros.css";
import { PARTNER_SPECIALTY_OPTIONS } from "../../utils/partnerApi";

const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";
const API_BASE = (RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || ""
);

function labelSpec(k) {
  return PARTNER_SPECIALTY_OPTIONS.find((o) => o.key === k)?.label || k;
}

export default function PartnersVerificationPage() {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [sel, setSel] = useState(null);
  const [approveSpecs, setApproveSpecs] = useState([]);
  const [adminNotes, setAdminNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const [statusFilter, setStatusFilter] = useState("pending_review,interview_required");

  const load = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const res = await fetch(`${API_BASE}/api/admin/partners${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Erreur");
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setMsg(String(e.message || "Erreur"));
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    if (!token || role !== "admin") return;
    load();
  }, [token, role, load]);

  const open = (row) => {
    setSel(row);
    setApproveSpecs([...(row.requestedSpecialties || [])]);
    setAdminNotes("");
  };

  const approve = async () => {
    if (!sel || !approveSpecs.length) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/partners/${sel._id}/approve`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ approvedSpecialties: approveSpecs, adminNotes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || "Erreur");
      setMsg("Partenaire approuvé.");
      setSel(null);
      load();
    } catch (e) {
      setMsg(String(e.message));
    } finally {
      setBusy(false);
    }
  };

  const refuse = async () => {
    if (!sel) return;
    const reason = window.prompt("Motif du refus ?") || "";
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/partners/${sel._id}/refuse`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason, adminNotes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Erreur");
      setMsg("Dossier refusé.");
      setSel(null);
      load();
    } catch (e) {
      setMsg(String(e.message));
    } finally {
      setBusy(false);
    }
  };

  const interview = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/partners/${sel._id}/interview`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          requireInterview: true,
          interviewStatus: "scheduled",
          interviewNotes: adminNotes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Erreur");
      setMsg("Statut « entretien requis » enregistré.");
      setSel(null);
      load();
    } catch (e) {
      setMsg(String(e.message));
    } finally {
      setBusy(false);
    }
  };

  const openProtectedDocument = async (relativeUrl) => {
    if (!relativeUrl) return;
    try {
      const res = await fetch(`${API_BASE}${relativeUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Impossible d'ouvrir le document.");
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      setMsg(String(e.message || "Erreur ouverture document"));
    }
  };

  if (!token || role !== "admin") {
    return <div style={{ padding: 40, textAlign: "center" }}>Accès admin requis.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h1 style={{ fontWeight: 900, marginBottom: 8 }}>ProMeet Partners — vérification</h1>
      <p style={{ color: "#555", marginBottom: 16 }}>
        Examinez les dossiers (identité, Kbis, RC pro), validez les spécialités et activez l&apos;accès Missions.
        Les nouvelles candidatures déclenchent un email aux comptes admin.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          { key: "pending_review,interview_required", label: "À traiter" },
          { key: "approved", label: "Approuvés" },
          { key: "", label: "Tous" },
        ].map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: statusFilter === f.key ? "2px solid #0d9488" : "1px solid #ccc",
              background: statusFilter === f.key ? "#ecfdf5" : "#fff",
              fontWeight: statusFilter === f.key ? 700 : 400,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      {msg && <div style={{ marginBottom: 12, padding: 10, background: "#f5f5f5", borderRadius: 8 }}>{msg}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: 8 }}>Statut</th>
                <th style={{ padding: 8 }}>Candidat</th>
                <th style={{ padding: 8 }}>Ville</th>
                <th style={{ padding: 8 }}>Spécialités demandées</th>
                <th style={{ padding: 8 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row._id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 8 }}>{row.status}</td>
                  <td style={{ padding: 8 }}>
                    {row.userId?.prenom} {row.userId?.nom}
                    <div style={{ fontSize: 12, color: "#666" }}>{row.userId?.email}</div>
                  </td>
                  <td style={{ padding: 8 }}>{row.city}</td>
                  <td style={{ padding: 8 }}>{(row.requestedSpecialties || []).map(labelSpec).join(", ")}</td>
                  <td style={{ padding: 8 }}>
                    <button type="button" onClick={() => open(row)}>Examiner</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div style={{ background: "#fff", borderRadius: 16, maxWidth: 520, width: "100%", padding: 20, maxHeight: "90vh", overflow: "auto" }}>
            <h2 style={{ marginTop: 0 }}>Dossier partenaire</h2>
            <p style={{ fontSize: 14, margin: "8px 0" }}>
              <strong>{sel.userId?.prenom} {sel.userId?.nom}</strong>
              <br />
              {sel.userId?.email} · {sel.userId?.telephone}
              <br />
              {sel.city} {sel.companyName ? `· ${sel.companyName}` : ""}
              {sel.siret ? ` · SIRET ${sel.siret}` : ""}
            </p>
            <p style={{ fontSize: 13, color: "#444" }}>
              <strong>Métier :</strong> {sel.currentProfession || "—"}
              <br />
              <strong>Expérience :</strong> {sel.yearsExperience ?? "—"} ans — {sel.experienceLevel || "—"}
              <br />
              <strong>Activité :</strong> {(sel.currentActivity || "").slice(0, 200)}
            </p>
            <p><strong>Documents :</strong></p>
            <ul style={{ fontSize: 13 }}>
              {sel.docUrls?.identityDocument && (
                <li>
                  <button
                    type="button"
                    style={{ background: "none", border: 0, color: "#0d9488", cursor: "pointer", padding: 0 }}
                    onClick={() => openProtectedDocument(sel.docUrls.identityDocument)}
                  >
                    Pièce d&apos;identité
                  </button>
                </li>
              )}
              {sel.docUrls?.siretProof && (
                <li>
                  <button
                    type="button"
                    style={{ background: "none", border: 0, color: "#0d9488", cursor: "pointer", padding: 0 }}
                    onClick={() => openProtectedDocument(sel.docUrls.siretProof)}
                  >
                    Extrait Kbis / SIRET
                  </button>
                </li>
              )}
              {sel.docUrls?.insuranceDocument && (
                <li>
                  <button
                    type="button"
                    style={{ background: "none", border: 0, color: "#0d9488", cursor: "pointer", padding: 0 }}
                    onClick={() => openProtectedDocument(sel.docUrls.insuranceDocument)}
                  >
                    Assurance RC pro
                  </button>
                </li>
              )}
              {!sel.docUrls?.identityDocument && <li style={{ color: "#b91c1c" }}>Identité manquante</li>}
              {!sel.docUrls?.siretProof && <li style={{ color: "#b91c1c" }}>Kbis manquant</li>}
              {!sel.docUrls?.insuranceDocument && <li style={{ color: "#b91c1c" }}>RC pro manquante</li>}
            </ul>
            <p><strong>Spécialités à approuver</strong> (cochez uniquement celles validées) :</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(sel.requestedSpecialties || []).map((k) => (
                <label key={k} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={approveSpecs.includes(k)}
                    onChange={() => {
                      setApproveSpecs((prev) =>
                        prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
                      );
                    }}
                  />
                  {labelSpec(k)}
                </label>
              ))}
            </div>
            <label style={{ display: "block", marginTop: 12 }}>
              <strong>Notes internes</strong>
              <textarea style={{ width: "100%", minHeight: 70 }} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <button type="button" disabled={busy} onClick={approve}>Approuver</button>
              <button type="button" disabled={busy} onClick={interview}>Entretien requis</button>
              <button type="button" disabled={busy} onClick={refuse}>Refuser</button>
              <button type="button" disabled={busy} onClick={() => setSel(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
